import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { CommunityAssessmentData } from '@/types/entities/clients/assessment';
import { Target, Lightbulb, TrendingUp, Star, Calendar, DollarSign } from 'lucide-react';

interface VisionGoalsProps {
  data: Partial<CommunityAssessmentData>;
  onUpdate: (data: Partial<CommunityAssessmentData>) => void;
  tier: 'silver' | 'gold' | 'custom';
}

export const Step_7_VisionGoals: React.FC<VisionGoalsProps> = ({ data, onUpdate, tier }) => {
  const updateField = (field: string, value: string | string[]) => {
    onUpdate({
      ...data,
      [field]: value,
    });
  };

  const updateGoalsSelection = (goal: string, checked: boolean) => {
    const currentGoals = data.primaryGoals || [];
    const updated = checked ? [...currentGoals, goal] : currentGoals.filter((g) => g !== goal);
    updateField('primaryGoals', updated);
  };

  const updateROIExpectations = (expectation: string, checked: boolean) => {
    const currentExpectations = data.roiExpectations || [];
    const updated = checked
      ? [...currentExpectations, expectation]
      : currentExpectations.filter((e) => e !== expectation);
    updateField('roiExpectations', updated);
  };

  const updateInvestmentInterests = (interest: string, checked: boolean) => {
    const currentInterests = data.investmentInterests || [];
    const updated = checked
      ? [...currentInterests, interest]
      : currentInterests.filter((i) => i !== interest);
    updateField('investmentInterests', updated);
  };

  const primaryGoalOptions = [
    'Improve financial transparency',
    'Streamline board operations',
    'Enhance resident communication',
    'Modernize voting processes',
    'Better maintenance management',
    'Increase resident engagement',
    'Reduce administrative burden',
    'Improve decision-making speed',
    'Enhance property values',
    'Strengthen community relationships',
    'Better compliance management',
    'Reduce operational costs',
    'Improve financial planning',
    'Modernize record keeping',
  ];

  const roiExpectationOptions = [
    'Reduced administrative time',
    'Lower operational costs',
    'Increased property values',
    'Improved resident satisfaction',
    'Better financial management',
    'Reduced legal/compliance costs',
    'More efficient decision making',
    'Improved vendor relationships',
    'Better reserve fund management',
    'Reduced board member turnover',
  ];

  const investmentInterestOptions = [
    'Basic communication tools',
    'Advanced voting systems',
    'Financial management suite',
    'Maintenance tracking',
    'Document management',
    'Resident portal',
    'Mobile applications',
    'Third-party integrations',
    'Custom reporting',
    'Training & support services',
    'Change management consulting',
    'Data migration services',
  ];

  const getTierBenefits = () => {
    switch (tier) {
      case 'silver':
        return [
          'Core community management features',
          'Basic voting & communication',
          'Standard financial reporting',
          'Email support',
        ];
      case 'gold':
        return [
          'Advanced voting & approval workflows',
          'Enhanced financial analytics',
          'Custom communication templates',
          'Priority support & training',
          'Basic integrations',
        ];
      case 'custom':
        return [
          'Fully customized platform',
          'Advanced integrations',
          'Dedicated account management',
          'Custom reporting & analytics',
          'White-label options',
          'Ongoing consulting',
        ];
      default:
        return [];
    }
  };

  const getVisionSummary = () => {
    const goals = data.primaryGoals?.length || 0;
    const hasVision = data.fiveYearVision && data.fiveYearVision.length > 10;
    const hasROI = data.roiExpectations?.length || 0;

    if (goals >= 3 && hasVision && hasROI >= 2) {
      return {
        level: 'comprehensive',
        color: 'green',
        message: 'Comprehensive vision with clear goals and ROI expectations',
      };
    } else if (goals >= 2 && (hasVision || hasROI >= 1)) {
      return {
        level: 'developing',
        color: 'yellow',
        message: 'Developing vision with some defined goals',
      };
    } else {
      return {
        level: 'initial',
        color: 'blue',
        message: "Initial vision - we'll help refine your goals together",
      };
    }
  };

  const visionSummary = getVisionSummary();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Vision & Goals</h3>
        <p className="text-gray-600">
          Let's define your community's future vision and success metrics
        </p>
      </div>

      <div className="space-y-6">
        {/* Primary Goals */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Primary Goals
            </CardTitle>
            <CardDescription>
              What are your main objectives for implementing a new system? (Select all that apply)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {primaryGoalOptions.map((goal) => (
                <div key={goal} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`goal-${goal}`}
                    checked={data.primaryGoals?.includes(goal) || false}
                    onChange={(e) => updateGoalsSelection(goal, e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor={`goal-${goal}`} className="text-sm">
                    {goal}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Five Year Vision */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              Long-term Vision
            </CardTitle>
            <CardDescription>Where do you see your community in 5 years?</CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="fiveYearVision">Five-Year Community Vision</Label>
              <Textarea
                id="fiveYearVision"
                value={data.fiveYearVision || ''}
                onChange={(e) => updateField('fiveYearVision', e.target.value)}
                placeholder="Describe your ideal future state - what does success look like? Consider resident engagement, financial health, operational efficiency, community satisfaction..."
                rows={5}
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* ROI Expectations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Return on Investment (ROI) Expectations
            </CardTitle>
            <CardDescription>
              What specific returns do you expect from this investment?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {roiExpectationOptions.map((expectation) => (
                <div key={expectation} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`roi-${expectation}`}
                    checked={data.roiExpectations?.includes(expectation) || false}
                    onChange={(e) => updateROIExpectations(expectation, e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor={`roi-${expectation}`} className="text-sm">
                    {expectation}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Investment Interests */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              Investment Priorities
            </CardTitle>
            <CardDescription>Which areas are you most interested in investing in?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {investmentInterestOptions.map((interest) => (
                <div key={interest} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`investment-${interest}`}
                    checked={data.investmentInterests?.includes(interest) || false}
                    onChange={(e) => updateInvestmentInterests(interest, e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor={`investment-${interest}`} className="text-sm">
                    {interest}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Discovery Call Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Discovery Call Preferences
            </CardTitle>
            <CardDescription>
              Let's schedule a personalized discussion about your vision
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferredMeetingTime">Preferred Meeting Time</Label>
                <Select
                  value={data.preferredMeetingTime || ''}
                  onValueChange={(value) => updateField('preferredMeetingTime', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select preferred time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning_weekday">Morning (9-12 PM) - Weekdays</SelectItem>
                    <SelectItem value="afternoon_weekday">Afternoon (1-5 PM) - Weekdays</SelectItem>
                    <SelectItem value="evening_weekday">Evening (6-8 PM) - Weekdays</SelectItem>
                    <SelectItem value="weekend_morning">Weekend Morning (9-12 PM)</SelectItem>
                    <SelectItem value="weekend_afternoon">Weekend Afternoon (1-5 PM)</SelectItem>
                    <SelectItem value="flexible">Flexible - any time works</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="urgency">Timeline to Connect</Label>
                <Select
                  value={data.timelineExpectation || ''}
                  onValueChange={(value) => updateField('timelineExpectation', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="When should we connect?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asap">ASAP - This week</SelectItem>
                    <SelectItem value="within_week">Within 1 week</SelectItem>
                    <SelectItem value="within_two_weeks">Within 2 weeks</SelectItem>
                    <SelectItem value="within_month">Within 1 month</SelectItem>
                    <SelectItem value="flexible">No rush - when convenient</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="specialRequirements">Special Requirements or Questions</Label>
              <Textarea
                id="specialRequirements"
                value={data.specialRequirements || ''}
                onChange={(e) => updateField('specialRequirements', e.target.value)}
                placeholder="Any specific topics you'd like to discuss, accessibility needs, or other stakeholders who should join the call..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Selected Tier Benefits */}
        <Card className="bg-linear-to-r from-purple-50 to-blue-50 border-purple-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-purple-600" />
              Your Selected Tier: {tier.charAt(0).toUpperCase() + tier.slice(1)}
            </CardTitle>
            <CardDescription>Here's what's included with your chosen tier</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-purple-800 mb-2">Included Features:</h4>
                <ul className="space-y-1">
                  {getTierBenefits().map((benefit, index) => (
                    <li key={index} className="text-sm text-purple-700 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-purple-800 mb-2">Next Steps:</h4>
                <ul className="space-y-1 text-sm text-purple-700">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                    Personalized demo & proposal
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                    ROI analysis based on your data
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                    Implementation timeline planning
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                    Custom stakeholder presentation
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vision Summary */}
        <Card className={`bg-${visionSummary.color}-50 border-${visionSummary.color}-200`}>
          <CardContent className="pt-6">
            <div className="text-center">
              <h4 className={`font-semibold text-${visionSummary.color}-800 mb-2`}>
                Vision Assessment:{' '}
                {visionSummary.level.charAt(0).toUpperCase() + visionSummary.level.slice(1)}
              </h4>
              <p className={`text-sm text-${visionSummary.color}-700 mb-3`}>
                {visionSummary.message}
              </p>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className={`text-lg font-bold text-${visionSummary.color}-800`}>
                    {data.primaryGoals?.length || 0}
                  </div>
                  <div className={`text-xs text-${visionSummary.color}-600`}>Primary Goals</div>
                </div>
                <div>
                  <div className={`text-lg font-bold text-${visionSummary.color}-800`}>
                    {data.roiExpectations?.length || 0}
                  </div>
                  <div className={`text-xs text-${visionSummary.color}-600`}>ROI Expectations</div>
                </div>
                <div>
                  <div className={`text-lg font-bold text-${visionSummary.color}-800`}>
                    {data.investmentInterests?.length || 0}
                  </div>
                  <div className={`text-xs text-${visionSummary.color}-600`}>Investment Areas</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Completion Message */}
      <div className="bg-linear-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 text-center">
        <h4 className="text-lg font-semibold text-green-800 mb-3">🎉 Assessment Complete!</h4>
        <p className="text-green-700 mb-4">
          Thank you for providing such detailed information about your community. This will help us
          create a personalized proposal that addresses your specific needs and goals.
        </p>
        <div className="bg-white rounded-lg p-4 border border-green-200">
          <p className="text-sm text-gray-700">
            <strong>What happens next:</strong>
          </p>
          <ol className="text-sm text-gray-700 mt-2 space-y-1 text-left max-w-md mx-auto">
            <li>1. We'll analyze your assessment responses</li>
            <li>2. Prepare a customized proposal and ROI analysis</li>
            <li>3. Schedule your discovery call within 24 hours</li>
            <li>4. Present a tailored solution for your community</li>
          </ol>
        </div>
      </div>
    </div>
  );
};
